/* eslint-disable react/jsx-sort-props */

import { useMediaQuery } from '@abyss/web/hooks/useMediaQuery';
import { styled } from '@abyss/web/tools/styled';
import { Checkbox } from '@abyss/web/ui/Checkbox';
import { Divider } from '@abyss/web/ui/Divider';
import { Flex } from '@abyss/web/ui/Flex';
import find from 'lodash/find';
import React, { useContext, useEffect, useRef, useState } from 'react';

import { adobeLinkTrackEvent } from '../../../../common/AdobeTagging/adobeLinkTrackEvent';
import {
  MAX_COMPARISON_ITEMS,
  SectionType,
} from '../../../../common/Constants';
import { ConstantsLagoon } from '../../../../common/ConstantsLagoon';
import { hideScrollbar } from '../../../../common/ConstantsStyles';
import { renderCompareLabel } from '../../../../common/DataCard/CompareTextUtils';
import { DataCardContainer } from '../../../../common/DataCard/DataCardContainer';
import { applyCheckboxChange } from '../../../../common/DataCard/DataCardUtils';
import { OutsideCountyAlert } from '../../../../common/DataCard/OutsideCountyAlert';
import { NationalGroupProvidersVirtualDataCard } from '../../../../common/NationalGroupProvidersVirtualBiteCard/NationalGroupProvidersVirtualDataCard';
import { getGeoLocationFromStorage } from '../../../../common/PSXHeader/SearchBar/utils';
import { CheckBoxDataCardStyle } from '../../../../common/ResultSection/ResultSection.style';
import { isOutOfCounty } from '../../../../common/SnackCardContainer/utils';
import { checkTerminationDate } from '../../../../common/Utils/terminateDateIndicatorUtils';
import { CountySearchContext } from '../../../../context/CountySearchContext';
import { useFeatureFlag } from '../../../../hooks/useFeatureFlag';
import { useLagoon } from '../../../../hooks/useLagoon';
import { StoreKeys } from '../../../../hooks/useStore/state';
import { useStore } from '../../../../hooks/useStore/useStore';
import { Provider } from '../../../../models/Provider';
import { ProviderType } from '../../../../models/ProviderDetails';
import { ResponseHeaders } from '../../../../models/ResponseHeaders';
import { isProviderStartDateInFuture } from '../../../../utils/date.utils';
import { scrollWithRef } from './CompareDrawer/utility/scrollWithRef';
import {
  AddToCompareLabelStyle,
  DataCardCheckBoxStyle,
} from './MapView.styled';

type Props = {
  headers: ResponseHeaders;
  sectionType: string;
  showCheckboxes: boolean;
  providerResults: any[];
  addSelectedProvider: (a: any) => void;
  expandedProviderListView?: boolean;
  updatePin: (a: any) => void;
  selectedCheckbox: any;
  setSelectedCheckbox: (a: any) => void;
  isMobileMap?: boolean;
  DataCardStyle?: Object;
  selectLocation?: (locationId, locationLng, locationLat) => void;
  map?: any;
  setRouteEndCoords?(coords: [number | null, number | null]): void;
  locationForAnalytics?: string;
  pageNumber?: number;
  parentRef?: any;
  selectedFilters?: string;
  searchTerm?: string;
  usedAt?: string;
  navigateToDirections?: boolean;
  setNavigateToDirections?: (a: boolean) => void;
  mobileRouteView?: boolean;
  fromMobileListView: boolean;
  isLoading: boolean;
  isMNR?: boolean;
  enableMapViewEnhancements?: boolean;
  isTieredPlan?: boolean;
  searchRadius?: string | number;
  isGroupedLocationDrawer?: boolean;
  currentPage?: number;
  drawerContent?: any;
  setIsCompareClicked?: () => void;
  compareShareToggle?: boolean;
  recommendationPreferences?: any;
  isMapView?: boolean;
};

export const DataCardListStyled = styled('div', {
  display: 'flex',
  flexWrap: 'wrap',
  boxSizing: 'border-box',
  alignItems: 'end',
  '@screen < $sm': {
    marginTop: '4px',
  },
  gap: '16px',
  zIndex: '0',
  variants: {
    variant: {
      flexDirectionRow: {
        flexDirection: 'row',
        overflowX: 'auto',
        flexWrap: 'nowrap',
      },
    },
  },
});

const getDataCardMaxWidth = (
  enableCompareButtonRelocation: boolean,
  isMapView: boolean,
  largePhoneScreen: boolean
): string =>
  !enableCompareButtonRelocation || isMapView
    ? '384px'
    : largePhoneScreen
    ? '328px'
    : '100%';

export const DataCardList = ({
  headers,
  sectionType,
  showCheckboxes,
  providerResults,
  addSelectedProvider,
  expandedProviderListView,
  updatePin,
  selectedCheckbox,
  setSelectedCheckbox,
  isMobileMap,
  selectLocation,
  map,
  setRouteEndCoords,
  DataCardStyle,
  locationForAnalytics,
  pageNumber,
  parentRef,
  selectedFilters,
  searchTerm,
  usedAt = '',
  navigateToDirections = false,
  setNavigateToDirections,
  mobileRouteView = false,
  fromMobileListView,
  isLoading,
  enableMapViewEnhancements,
  isMNR,
  isTieredPlan,
  searchRadius,
  isGroupedLocationDrawer = false,
  currentPage,
  drawerContent,
  setIsCompareClicked,
  compareShareToggle,
  recommendationPreferences,
  isMapView = false,
}: Props) => {
  const checkedCount = Object.keys(selectedCheckbox.checked).filter(
    (key) => selectedCheckbox.checked[key]
  ).length;
  const largePhoneScreen = useMediaQuery(
    '(min-width: 439px) and (max-width: 486px)'
  );
  const showRelocatedCompare = useMediaQuery('(min-width: 439px)');

  // begin: a11y keyboard navigation
  const [lastSelectedIndex, setLastSelectedIndex] = useState<number>(-1);
  const uiState = useStore(StoreKeys.UI_STATE);

  const { compareProvidersFlow, shareProvidersFlow } = useStore(
    StoreKeys.UI_STATE
  );
  const { indexOflastProviderSelected } = compareProvidersFlow;
  const setUIstate = useStore(StoreKeys.SET_UI_STATE);

  const [
    displayTerminationDateEnabled,
    enableMapViewHideOptionFF,
    countySearchAlertFlag,
    enableCompareSmartChoiceFF,
  ] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.DISPLAY_FUTURE_TERMINATION_DATE,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_MAP_VIEW_HIDE_OPTION,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_COUNTY_SEARCH_ALERT,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_COMPARE_SMART_CHOICE,
  ]);

  const enableCompareSmartChoice =
    enableCompareSmartChoiceFF && sectionType === SectionType.PROVIDER;

  const enableMapViewHideOption: boolean =
    isGroupedLocationDrawer || enableMapViewHideOptionFF;
  const uiMessagingData = useLagoon('ui-messaging')();
  const addToCompareText = find(uiMessagingData, {
    key: ConstantsLagoon.UI_MESSAGING.COMPARE.COMPARE_ADD_TO_COMPARE_TEXT,
  })?.message;
  const compareXNowLinkText = find(uiMessagingData, {
    key: ConstantsLagoon.UI_MESSAGING.COMPARE.COMPARE_LINK_COMPARE_NOW,
  })?.message;
  const selectMoreToCompareText = find(uiMessagingData, {
    key: ConstantsLagoon.UI_MESSAGING.COMPARE
      .COMPARE_SELECT_MORE_TO_COMPARE_TEXT,
  })?.message;
  const allConfigs = useLagoon('config')();

  const compareButtonRelocation = allConfigs?.find(
    (row) => row.key === 'ENABLE_COMPARE_BUTTON_RELOCATION'
  )?.value;

  const enableCompareButtonRelocation =
    compareButtonRelocation === 'Y' ? true : false;

  useEffect(() => {
    if (indexOflastProviderSelected === -1 || lastSelectedIndex >= 0) {
      const newUIState = {
        ...uiState,
        compareProvidersFlow: {
          ...compareProvidersFlow,
          indexOflastProviderSelected: lastSelectedIndex + 1,
        },
        shareProvidersFlow: {
          ...shareProvidersFlow,
          indexOflastProviderSelected: lastSelectedIndex + 1,
        },
      };
      setUIstate(newUIState);
    }
  }, [lastSelectedIndex]);
  // end: a11y keyboard navigation

  const handleDirectionClick = (provider) => {
    if (selectLocation)
      selectLocation(
        provider.locationId,
        provider.longitude,
        provider.latitude
      );
  };

  const listRef = useRef<HTMLDivElement>(null);
  const processingClicksRef = useRef<Set<string>>(new Set());

  const scrollIntoView = (index: number) => {
    scrollWithRef(listRef, index);
    parentRef?.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  };

  const { isCountySearch } = useContext(CountySearchContext);
  const { name, stateCode } = getGeoLocationFromStorage();

  const adobeLinkTrackEventCheckBox = (checkedState: any, index: number) => {
    adobeLinkTrackEvent({
      name: `provider${checkedState ? ':uncheck' : ':checked'}`,
      location: `body:${sectionType} results:page ${pageNumber}:position ${
        index + 1
      }`,
      type: 'checkbox',
    });
  };

  const displayOutOfCntyIndicator = (provider) => {
    const isOutOfCnty = isOutOfCounty(
      isCountySearch,
      name,
      stateCode,
      provider
    );

    return (
      countySearchAlertFlag &&
      isOutOfCnty && (
        <OutsideCountyAlert
          county={name?.split(',')[0]}
          isOutOfCounty={isOutOfCnty}
        />
      )
    );
  };
  const isNetworkAlertIndicator = providerResults.some((snackCardProvider) =>
    checkTerminationDate(
      displayTerminationDateEnabled,
      snackCardProvider?.networkEndDate
    )
  );
  const isInNetworkBadge = providerResults.some((snackCardProvider) =>
    isProviderStartDateInFuture(snackCardProvider?.networkStartDate)
  );

  const displayVirtualProvider = (provider: Provider) =>
    !provider.address &&
    !!provider?.virtualCareOffered?.length &&
    (provider.providerType === ProviderType.PRACTITIONER ||
      provider.providerType === ProviderType.PROVIDER_GROUP);

  const displayDataCard = (provider, index) =>
    displayVirtualProvider(provider) ? (
      <NationalGroupProvidersVirtualDataCard
        css={DataCardStyle}
        fromMobileListView={fromMobileListView}
        headers={headers}
        index={index}
        isLoading={isLoading}
        key={provider?.locationId}
        locationForAnalytics={locationForAnalytics}
        practitioner={provider}
        sectionType={sectionType}
      />
    ) : (
      <div
        data-auto-testid="data-card-container"
        data-testid="data-card-container"
        key={provider?.locationId}
        style={{
          width: enableMapViewHideOption ? window.innerWidth - 32 : '',
        }}
      >
        <DataCardContainer
          addSelectedProvider={addSelectedProvider}
          adobeLinkTrackEventCheckBox={adobeLinkTrackEventCheckBox}
          compareShareToggle={compareShareToggle}
          css={DataCardStyle}
          currentPage={currentPage}
          data-auto-testid="data-card"
          data-testid="data-card"
          drawerContent={drawerContent}
          enableMapViewHideOption={enableMapViewHideOption}
          expandedProviderListView={expandedProviderListView}
          fromMobileListView={fromMobileListView}
          handleDirectionClick={() => handleDirectionClick(provider)}
          headers={headers}
          index={index}
          isCheckboxDisabled={isCheckboxDisabled}
          isGroupedLocationDrawer={isGroupedLocationDrawer}
          isInNetworkBadge={isInNetworkBadge}
          isLoading={isLoading}
          isMapView={isMapView}
          isMNR={isMNR}
          isNetworkAlertIndicator={isNetworkAlertIndicator}
          isTieredPlan={isTieredPlan}
          locationForAnalytics={locationForAnalytics}
          map={map}
          mobileRouteView={mobileRouteView}
          navigateToDirections={navigateToDirections}
          pageNumber={pageNumber}
          practitioner={provider}
          providerList={providerResults}
          recommendationPreferences={recommendationPreferences}
          scrollIntoView={scrollIntoView}
          searchRadius={searchRadius}
          searchTerm={searchTerm}
          sectionType={sectionType}
          selectedCheckbox={selectedCheckbox}
          selectedFilters={selectedFilters}
          setIsCompareClicked={setIsCompareClicked}
          setLastSelectedIndex={setLastSelectedIndex}
          setNavigateToDirections={setNavigateToDirections}
          setRouteEndCoords={setRouteEndCoords}
          setSelectedCheckbox={setSelectedCheckbox}
          updatePin={updatePin}
          usedAt={usedAt}
        />
        {displayOutOfCntyIndicator(provider)}
        {enableMapViewHideOption && (
          <Divider
            color={
              enableCompareButtonRelocation && showCheckboxes
                ? '$gray4'
                : '$neutralGray2'
            }
            margin={expandedProviderListView ? '0' : undefined}
          />
        )}
      </div>
    );

  const isCheckboxDisabled = (provider: any, selectedCheckbox: any) =>
    (!selectedCheckbox.checked[provider?.locationId] &&
      checkedCount >= MAX_COMPARISON_ITEMS) ||
    !provider?.latitude;

  const handleCheckboxChange = (provider: any, index: number) => {
    if (isCheckboxDisabled(provider, selectedCheckbox)) return;

    if (processingClicksRef.current.has(provider?.locationId)) return;

    processingClicksRef.current.add(provider?.locationId);
    applyCheckboxChange(
      provider,
      index,
      selectedCheckbox,
      setSelectedCheckbox,
      addSelectedProvider,
      adobeLinkTrackEventCheckBox,
      setLastSelectedIndex
    );
    setTimeout(() => {
      processingClicksRef.current.delete(provider?.locationId);
    }, 100);
  };

  const handleCheckboxFocus = (event: React.FocusEvent<HTMLElement>) => {
    const element = event.currentTarget;
    const isKeyboardFocus = event.target.matches(':focus-visible');

    if (element && isKeyboardFocus) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'nearest',
      });
    }
  };

  const getCompareCheckboxAriaLabel = (provider: any) => {
    const parts = ['Add to compare'];

    // Provider name
    if (provider?.providerName) {
      parts.push(provider.providerName);
    }

    // Specialty
    if (provider?.speciality) {
      parts.push(provider.speciality);
    }

    // Smart choice score
    if (provider?.recommendationDetails?.totalRecommendationScore) {
      parts.push(
        `Smart choice ${provider.recommendationDetails.totalRecommendationScore}%`
      );
    }

    return parts.join(' ');
  };

  const renderCompareText = (
    provider: any,
    textAlign: 'center' | 'left' = 'left',
    extraCss: object = {}
  ) =>
    renderCompareLabel(
      provider,
      selectedCheckbox,
      compareXNowLinkText,
      addToCompareText,
      selectMoreToCompareText,
      setIsCompareClicked,
      enableCompareButtonRelocation,
      textAlign,
      extraCss
    );

  const renderAriaLabel = (provider: any) => (
    <span
      id={`compare-checkbox-label-${provider?.locationId}`}
      style={{
        position: 'absolute',
        width: '1px',
        height: '1px',
        padding: 0,
        margin: '-1px',
        overflow: 'hidden',
        clip: 'rect(0,0,0,0)',
        whiteSpace: 'nowrap',
        border: 0,
      }}
    >
      {getCompareCheckboxAriaLabel(provider)}
    </span>
  );

  const renderDataCardBlock = (
    provider: any,
    index: number,
    showInlineDivider: boolean = true
  ) => (
    <React.Fragment>
      <DataCardContainer
        css={CheckBoxDataCardStyle}
        currentPage={currentPage}
        drawerContent={drawerContent}
        enableMapViewHideOption={enableMapViewHideOption}
        expandedProviderListView={expandedProviderListView}
        fromMobileListView={fromMobileListView}
        handleDirectionClick={() => handleDirectionClick(provider)}
        headers={headers}
        index={index}
        isGroupedLocationDrawer={isGroupedLocationDrawer}
        isLoading={isLoading}
        isMapView={isMapView}
        isMNR={isMNR}
        isTieredPlan={isTieredPlan}
        key={provider?.locationId}
        locationForAnalytics={locationForAnalytics}
        pageNumber={pageNumber}
        practitioner={provider}
        recommendationPreferences={recommendationPreferences}
        scrollIntoView={scrollIntoView}
        searchRadius={searchRadius}
        searchTerm={searchTerm}
        sectionType={sectionType}
        selectedFilters={selectedFilters}
      />
      {showInlineDivider && (
        <div style={{ marginRight: '0', width: '100%' }}>
          <Divider
            color={enableCompareButtonRelocation ? '$gray4' : '$gray3'}
          />
        </div>
      )}
      {displayOutOfCntyIndicator(provider)}
    </React.Fragment>
  );

  const renderCheckboxCard = (provider: any, index: number) => {
    const showCompareLabel =
      (enableMapViewHideOption &&
        !expandedProviderListView &&
        enableCompareSmartChoice &&
        !isGroupedLocationDrawer) ||
      compareShareToggle;

    if (enableCompareButtonRelocation && showRelocatedCompare) {
      return (
        <React.Fragment key={provider?.locationId}>
          <Flex css={{ alignItems: 'stretch', gap: '2px', width: '100%' }}>
            <div style={{ alignSelf: 'center' }}>
              <Checkbox
                css={{
                  'abyss-checkbox-root': {
                    width: '54px',
                    marginRight: '10px',
                    flexShrink: 0,
                    '& > div': {
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '8px',
                    },
                  },
                  'abyss-checkbox-label': {
                    padding: 0,
                    textAlign: 'center',
                    width: '54px',
                  },
                }}
                data-auto-testid="map-view-checkbox-group"
                data-testid="map-view-checkbox-group"
                isChecked={
                  selectedCheckbox.checked[provider?.locationId] || false
                }
                isDisabled={isCheckboxDisabled(provider, selectedCheckbox)}
                label={
                  <div
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                  >
                    {renderAriaLabel(provider)}
                    {showCompareLabel && (
                      <Flex
                        aria-hidden="true"
                        css={{ justifyContent: 'flex-start', width: '54px' }}
                      >
                        {renderCompareText(provider, 'center')}
                      </Flex>
                    )}
                  </div>
                }
                onChange={() => {
                  requestAnimationFrame(() => {
                    handleCheckboxChange(provider, index);
                  });
                }}
                onFocus={handleCheckboxFocus}
                size={18}
              />
            </div>
            {enableMapViewHideOption && (
              <Divider
                color="$gray3"
                height="fill"
                margin={0}
                orientation="vertical"
                width="1px"
              />
            )}
            <div
              aria-hidden="true"
              style={{
                flex: 1,
                maxWidth: getDataCardMaxWidth(
                  enableCompareButtonRelocation,
                  isMapView,
                  largePhoneScreen
                ),
              }}
            >
              {renderDataCardBlock(provider, index, false)}
            </div>
          </Flex>
          <div style={{ marginRight: '0', width: '100%' }}>
            <Divider
              color={enableCompareButtonRelocation ? '$gray4' : '$gray3'}
            />
          </div>
        </React.Fragment>
      );
    }

    return (
      <Checkbox
        css={{
          ...DataCardCheckBoxStyle,
          'abyss-checkbox-root': {
            ...DataCardCheckBoxStyle['abyss-checkbox-root'],
            ...((enableMapViewHideOption &&
              !expandedProviderListView &&
              !isGroupedLocationDrawer) ||
            enableCompareButtonRelocation
              ? {
                  '& > div': {
                    flexDirection: 'column',
                    gap: enableCompareButtonRelocation ? '4px' : '12px',
                    alignItems: 'normal !important',
                  },
                  '.abyss-checkbox-container': {
                    marginLeft: '20px',
                    '.abyss-icon-symbol': { marginLeft: '0px' },
                  },
                }
              : {}),
          },
        }}
        data-auto-testid="map-view-checkbox-group"
        data-testid="map-view-checkbox-group"
        isChecked={selectedCheckbox.checked[provider?.locationId] || false}
        isDisabled={isCheckboxDisabled(provider, selectedCheckbox)}
        key={provider?.locationId}
        label={
          <div
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            {renderAriaLabel(provider)}
            {showCompareLabel && (
              <Flex
                css={{
                  ...AddToCompareLabelStyle(enableCompareButtonRelocation),
                }}
                aria-hidden="true"
              >
                {renderCompareText(provider, 'left', { marginLeft: '2px' })}
              </Flex>
            )}
            <div aria-hidden="true">{renderDataCardBlock(provider, index)}</div>
          </div>
        }
        onChange={() => {
          requestAnimationFrame(() => {
            handleCheckboxChange(provider, index);
          });
        }}
        onFocus={handleCheckboxFocus}
        size={18}
      />
    );
  };

  return (
    <DataCardListStyled
      css={{
        gap:
          expandedProviderListView || isGroupedLocationDrawer ? '0px' : '16px',
        ...(!expandedProviderListView &&
          !isGroupedLocationDrawer &&
          enableMapViewHideOption && {
            paddingRight: '8px',
            marginLeft: '-5px',
            '@media (max-width: 400px)': {
              paddingRight: '16px',
            },
          }),
        '@screen < $md': {
          display: enableMapViewHideOption
            ? 'flex'
            : enableMapViewEnhancements
            ? 'grid'
            : 'flex',
        },
        '@screen < $sm': { hideScrollbar },
      }}
      data-auto-testid="data-card-list-flex"
      data-testid="data-card-list-flex"
      ref={listRef}
      variant={isMobileMap ? 'flexDirectionRow' : undefined}
    >
      {!expandedProviderListView &&
        !isGroupedLocationDrawer &&
        enableMapViewHideOption && (
          <div style={{ marginRight: '0', width: '100%' }}>
            <Divider
              color={enableCompareButtonRelocation ? '$gray4' : '$gray3'}
            />
          </div>
        )}
      {(expandedProviderListView || isGroupedLocationDrawer) &&
        providerResults.map((provider: any, index) =>
          displayDataCard(provider, index)
        )}
      {!expandedProviderListView &&
        !isGroupedLocationDrawer &&
        providerResults.map((provider: any, index) => {
          if (!showCheckboxes) return displayDataCard(provider, index);
          return renderCheckboxCard(provider, index);
        })}
    </DataCardListStyled>
  );
};
